import React from 'react';
import LegalPage from './LegalPage';

export default function PrivacyPolicy() {
  const content = (
    <div>
      <p className="text-gray-600 mb-6">
        <strong>Effective Date:</strong> October 1, 2025
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Introduction</h2>
      <p className="mb-4">
        EQ Community INC ("EQ," "we," "us," or "our") operates the EQ Assistant platform at 
        https://www.eqassistant.com (the "Platform"). We are committed to protecting your privacy 
        and ensuring transparency in how we collect, use, and safeguard your personal information.
      </p>
      <p className="mb-4">
        This Privacy Policy explains how we collect, use, disclose, and protect your information 
        when you use our Platform. By accessing or using the Platform, you agree to this Privacy Policy.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Information We Collect</h2>
      
      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">1.1 Information You Provide Directly</h3>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li>
          <strong>Account Information:</strong> Name and email address (collected through EQ.app identity partners), 
          OAuth provider information, subscription and billing information (processed by EQ.app payment partners)
        </li>
        <li>
          <strong>Search Queries and Inputs:</strong> Natural language search queries, job descriptions, 
          company and candidate preferences, saved searches and campaigns
        </li>
        <li>
          <strong>Communications:</strong> Support requests, feedback and survey responses
        </li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">1.2 Information We Collect Automatically</h3>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li><strong>Usage Data:</strong> Pages visited, features used, search history, time spent</li>
        <li><strong>Technical Data:</strong> IP address, browser type, device information, operating system</li>
        <li><strong>Cookies:</strong> Authentication cookies, session management, functional cookies</li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">1.3 Information from Third Parties</h3>
      <p className="mb-4">
        We receive professional contact and company information from trusted EQ.app data partners, AI-powered analysis from EQ.app intelligence partners, 
        and public company information curated by EQ.app research partners.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. How We Use Your Information</h2>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li>Provide lead generation and candidate search services</li>
        <li>Create and manage your account</li>
        <li>Process subscription payments</li>
        <li>Analyze usage to improve Platform functionality</li>
        <li>Send service-related notifications</li>
        <li>Comply with legal obligations</li>
        <li>Detect and prevent fraud and security incidents</li>
      </ul>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Legal Basis for Processing (GDPR)</h2>
      <p className="mb-4">
        For users in the European Economic Area (EEA), UK, and Switzerland, we process your personal 
        data based on:
      </p>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li><strong>Contract Performance:</strong> Processing necessary to provide our services</li>
        <li><strong>Legitimate Interests:</strong> Analytics, fraud prevention, service improvement</li>
        <li><strong>Consent:</strong> Marketing communications, optional cookies</li>
        <li><strong>Legal Obligation:</strong> Compliance with applicable laws</li>
      </ul>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. How We Share Your Information</h2>
      <p className="mb-4">
        <strong>We do not sell your personal information.</strong> We share information only with:
      </p>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li><strong>Service Providers:</strong> Trusted EQ.app partners who power authentication, payment processing, intelligent enrichment, and data services</li>
        <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
        <li><strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets</li>
        <li><strong>With Your Consent:</strong> For other purposes with your explicit consent</li>
      </ul>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Data Retention</h2>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li><strong>Account Data:</strong> While account is active and for 90 days after deletion</li>
        <li><strong>Search History:</strong> 24 months or until account deletion</li>
        <li><strong>Lead Data:</strong> While campaigns are active and for 90 days after deletion</li>
        <li><strong>Usage Logs:</strong> 90 days</li>
        <li><strong>Billing Records:</strong> 7 years (legal requirement)</li>
      </ul>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Your Rights and Choices</h2>
      
      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">For All Users:</h3>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li><strong>Access:</strong> Request a copy of your personal data</li>
        <li><strong>Correction:</strong> Update or correct inaccurate information</li>
        <li><strong>Deletion:</strong> Request deletion of your personal data</li>
        <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Additional Rights for EEA/UK/Swiss Users (GDPR):</h3>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li><strong>Right to Restriction:</strong> Request limitation of processing</li>
        <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
        <li><strong>Right to Portability:</strong> Receive data in machine-readable format</li>
        <li><strong>Right to Lodge a Complaint:</strong> File complaint with data protection authority</li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">California Privacy Rights (CCPA):</h3>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li><strong>Right to Know:</strong> Request disclosure of personal information collected</li>
        <li><strong>Right to Delete:</strong> Request deletion of personal information</li>
        <li><strong>Right to Opt-Out:</strong> We do not sell personal information</li>
        <li><strong>Right to Non-Discrimination:</strong> Equal service regardless of privacy rights exercise</li>
      </ul>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Data Security</h2>
      <p className="mb-4">
        We implement industry-standard security measures:
      </p>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li>TLS/SSL encryption for data in transit</li>
        <li>Encrypted database connections</li>
        <li>Row-level security (RLS) for multi-tenant data isolation</li>
        <li>JWT-based authentication with rotation</li>
        <li>Regular security audits and assessments</li>
      </ul>
      <p className="mb-4 text-gray-600 italic">
        No security system is impenetrable. While we use reasonable measures to protect your data, 
        we cannot guarantee absolute security.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. International Data Transfers</h2>
      <p className="mb-4">
        EQ Community INC is based in the United States. For EEA, UK, and Swiss users, we rely on 
        Standard Contractual Clauses (SCCs) approved by the European Commission for international 
        data transfers.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Children's Privacy</h2>
      <p className="mb-4">
        Our Platform is not intended for users under 18 years of age. We do not knowingly collect 
        personal information from children under 18.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Changes to This Privacy Policy</h2>
      <p className="mb-4">
        We may update this Privacy Policy periodically. We will notify you of material changes by 
        posting the updated policy with a new "Last Updated" date and sending an email to your 
        registered address for significant changes.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Contact Us</h2>
      <p className="mb-4">
        If you have questions about this Privacy Policy or our data practices, please contact us:
      </p>
      <div className="bg-gray-100 rounded-lg p-4 mb-4">
        <p className="mb-2"><strong>EQ Community INC</strong></p>
        <p className="mb-1">Email: <a href="mailto:hello@eq.community" className="text-indigo-600 hover:underline">hello@eq.community</a></p>
        <p className="mb-1">Support: <a href="mailto:hello@eq.community" className="text-indigo-600 hover:underline">hello@eq.community</a></p>
        <p className="mb-1">Data Protection Officer: <a href="mailto:hello@eq.community" className="text-indigo-600 hover:underline">hello@eq.community</a></p>
      </div>
      <p className="text-gray-600 italic">
        We will respond to all inquiries within 5 business days.
      </p>

      <div className="mt-8 pt-6 border-t border-gray-200 text-sm text-gray-600">
        <p><strong>Version:</strong> 1.0</p>
        <p><strong>Effective Date:</strong> October 1, 2025</p>
        <p><strong>Last Updated:</strong> October 1, 2025</p>
      </div>
    </div>
  );

  return (
    <LegalPage
      title="Privacy Policy"
      content={content}
      lastUpdated="October 1, 2025"
    />
  );
}
